import type { FC } from 'react';

import { useCms, useContent, useImageMapper, useSetting } from '@eneco/cms';
import { useChat } from '@eneco/components/chat/useChat';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Badge, Box, Button, Card, Form, Grid, Heading, Hidden, Image, InputText, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import { ChatIcon, SendIcon } from '@eneco/sparky/icons';
import type { HeadingProps } from '@eneco/sparky/types';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type { ChatLinksContent } from './chat-links-content.types';

interface FormFields {
  question: string;
}

const findByRole = <T extends { role?: string }>(entries: T[] | undefined, role: string): T | undefined => entries?.find(entry => entry.role === role);

/**
 * `ChatLinks` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/ChatLinks/ChatLinks.tsx`): a tag,
 * title, info text, a row of pre-defined chat-topic links, a free-text question form that opens the chat
 * widget, and an optional image.
 *
 * See `chat-links-content.types.ts` — the pre-defined chat-topic links (`items`) and topic-aware
 * question submission (`defaultChatTopic`) have no resolvable content on `?_cms=sanity` yet, so both are
 * Sitecore-only today; `richText`/`formFields` role-collapse the same way.
 */
const ChatLinks: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ChatLinksContent>();
  const mapImage = useImageMapper();
  const [mount] = useChat();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormFields>();

  const headingLevel = (useSetting('headingLevel') || 'h2') as HeadingProps['as'];
  const chatLinksPlacement = useSetting('chatLinksPlacement');
  const chatLinksColumnCount = Number.parseInt(useSetting('chatLinksColumnCount') ?? '', 10) || 3;

  const infoText = findByRole(content.richText, 'infoText')?.content ?? content.infoText;
  const questionFormField = findByRole(content.formFields, 'questionFormField') ?? content.questionFormField;
  const infoHtml = adapter.toRichText(infoText);
  const imageProps = mapImage(content.image);
  const preDefinedTopics = (content.items ?? []).filter(item => item.displayText && item.chatTopic);

  const submitCustomQuestion: SubmitHandler<FormFields> = ({ question }) => {
    const sendTopicAndAskText = content.defaultChatTopic?.sendDisplayText;
    mount({
      visibility: 'open',
      topic: sendTopicAndAskText ? content.defaultChatTopic?.name : undefined,
      askText: question,
      sendTopicAndAskText,
    });
    reset();
  };

  return (
    <Card display="block" elevation="L">
      <Stack direction="row">
        <Stack.Item grow>
          <Box paddingY="8" paddingLeft="8" paddingRight={{ initial: '8', lg: undefined }}>
            <Stack gap="6">
              {content.tag && (
                <Stack.Item>
                  <Badge>{content.tag}</Badge>
                </Stack.Item>
              )}
              {content.title && (
                <Heading as={headingLevel} size="M">
                  {content.title}
                </Heading>
              )}
              {infoHtml && (
                <Text>
                  <RichTextComponent html={infoHtml} />
                </Text>
              )}
              <Grid columns="1" rowGap="8">
                <Grid.Item gridRow={chatLinksPlacement === 'Below' ? '2' : '1'}>
                  {preDefinedTopics.length > 0 && (
                    <Box paddingTop="2">
                      <Grid
                        alignY="start"
                        columnGap="8"
                        gridTemplateColumns={{ initial: 'auto', lg: 'auto auto', xl: 'auto '.repeat(chatLinksColumnCount) }}
                        rowGap="6">
                        {preDefinedTopics.map(item => (
                          <TextLink
                            leftIcon={<ChatIcon />}
                            emphasis="medium"
                            onClick={() => mount({ visibility: 'open', topic: item.chatTopic, askText: item.displayText, sendTopicAndAskText: item.sendDisplayText })}
                            key={item.id ?? item.displayText}>
                            {item.displayText}
                          </TextLink>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Grid.Item>
                <Grid.Item>
                  <Form onSubmit={handleSubmit(submitCustomQuestion)}>
                    <Stack direction={{ initial: 'column', md: 'row' }} gap="3" alignY="end">
                      <Stack.Item grow>
                        <InputText
                          label={questionFormField?.label ?? ''}
                          hint={questionFormField?.hint}
                          error={errors.question ? (questionFormField?.validationMessage ?? '') : ''}
                          {...register('question', { maxLength: 75 })}
                          placeholder={questionFormField?.placeholder}
                        />
                      </Stack.Item>
                      {content.buttonText && (
                        <Stretch width={{ initial: true, md: false }} height={false}>
                          <Button type="submit" size="compact">
                            <SendIcon />
                            {content.buttonText}
                          </Button>
                        </Stretch>
                      )}
                    </Stack>
                  </Form>
                </Grid.Item>
              </Grid>
            </Stack>
          </Box>
        </Stack.Item>
        {imageProps?.src && (
          <Hidden below="lg">
            <Stack.Item shrink={false}>
              <Image src={imageProps.src} alt={imageProps.alt ?? ''} objectFit="cover" maxWidth={{ lg: '320px', xl: '400px' }} height="100%" />
            </Stack.Item>
          </Hidden>
        )}
      </Stack>
    </Card>
  );
};

export default ChatLinks;
